import { useQuery } from "@tanstack/react-query";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import type { TeamListItem } from "../types/teams.types";
import { teamHasFilters } from "../utils/teamHasFilters";

import { teamsQueryKeys } from "./queryKeyFactory";

type UseTeamsListQueryParams<TData = readonly []> = {
    enabled?: boolean;
    onlyMyTeams?: boolean;
    select?: (teams: readonly TeamListItem[]) => TData;
};

// Only teams that actually have filters constitute a territory — shared so every consumer derives the
// same set from the cache (no duplicated filtering at call sites).
export const selectTeamsWithFilters = (teams: readonly TeamListItem[]): readonly TeamListItem[] =>
    teams.filter((team) => teamHasFilters(team.filters));

export const useTeamsListQuery = <TData = TeamListItem[]>({
    enabled = true,
    onlyMyTeams = false,
    select,
}: UseTeamsListQueryParams<TData> = {}) => {
    return useQuery<readonly TeamListItem[], Error, TData>({
        queryKey: onlyMyTeams ? teamsQueryKeys.myTeams() : teamsQueryKeys.all(),
        queryFn: () => salesApiService.teams.fetchTeams(onlyMyTeams ? { onlyMyTeams: true } : undefined),
        enabled,
        select,
        // `myTeams` feeds territory filtering and may be edited by one user and read by another
        refetchOnMount: onlyMyTeams ? "always" : false,
    });
};
